import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Admin MFA state.
 *
 * Deliberately holds no token — the session token lives in configStore, in
 * memory, per the project rule that tokens never enter serialised redux state.
 * What lives here is the deadline, for the countdown and the expiry warnings.
 *
 * Which warnings have already been shown is *not* here. That is per-deadline
 * state owned by useAdminSessionWatcher, because it has no meaning outside the
 * deadline it belongs to — a boolean here used to survive a dismissal and
 * silence every later warning for the same session.
 *
 * This slice must stay in PERSIST_BLACKLIST (redux/store.ts). A persisted
 * `valid` would survive a reload and render admin chrome for a session the
 * server has already expired; the correct behaviour is `unknown` on load until
 * GET admin/access answers.
 */

export type AdminMfaStatus = 'unknown' | 'checking' | 'valid' | 'required' | 'error';

export type AdminMfaState = {
    status: AdminMfaStatus,
    // The member locked Admin themselves, rather than the session lapsing.
    isMemberLocked: boolean,
    canAccessAdmin: boolean,
    canAccessWelfareAdmin: boolean,
    /** Server-issued deadline, ISO 8601. Display only. */
    expiresAt?: string,
    /**
     * Remaining life as the server measured it, at the moment the response was
     * received. The countdown anchors on this plus performance.now() rather
     * than on expiresAt, so a system clock change — or a laptop resuming from
     * sleep — cannot move the deadline.
     */
    expiresInSeconds: number,
    /** Monotonic timestamp at which expiresInSeconds was read. */
    receivedAtMs?: number,
    warningSeconds: number,
    /**
     * The warning ladder, largest first — 20 / 10 / 5 minutes by default. Each
     * rung fires once per deadline, so dismissing one still leaves the next.
     * Server-supplied so the local timing override shortens all three together.
     */
    warningSchedule: number[],
    /** Masked destination of the last challenge, e.g. a****n@example.org. */
    maskedEmail?: string,
    /** Server-authoritative resend cooldown. Never computed client-side. */
    nextAllowedAt?: string,
    codeExpiresAt?: string,
    /**
     * Local development only. The Core service returns the plaintext code
     * instead of emailing it when ADMIN_MFA_DEV_MODE is set AND FLASK_ENV is a
     * local environment, so the flow can be tested without a mailbox. It is
     * never populated by a deployed environment.
     */
    devCode?: string,
    /**
     * When the wrong-code lockout lifts, as an epoch millisecond value.
     *
     * In redux rather than in useAdminMfaChallenge because the hook's state
     * does not outlive the modal. The lockout is reported once -- on the 429,
     * or on the challenge after a reload -- and if the component remounts
     * around that moment the flag is lost and the member is shown a form they
     * cannot use. Here it survives, and the modal can seed itself from it
     * before any request comes back.
     *
     * A timestamp rather than a number of seconds so it stays correct however
     * long it sits there.
     */
    blockedUntilMs?: number
}

const initialState: AdminMfaState = {
    status: 'unknown',
    isMemberLocked: false,
    canAccessAdmin: false,
    canAccessWelfareAdmin: false,
    expiresInSeconds: 0,
    warningSeconds: 600,
    warningSchedule: [1200, 600, 300]
};

export type AdminMfaSessionPayload = {
    valid?: boolean,
    /**
     * Seconds left on the wrong-code lockout, reported by /access.
     *
     * That call runs on every authenticated page load and is never MFA-gated,
     * so it is the earliest the client can know -- early enough for the modal
     * to render locked on its first paint instead of waiting on the challenge
     * request that follows it.
     */
    // eslint-disable-next-line camelcase
    blocked_in_seconds?: number,
    // eslint-disable-next-line camelcase
    expires_at?: string,
    // eslint-disable-next-line camelcase
    expires_in_seconds?: number,
    // eslint-disable-next-line camelcase
    // eslint-disable-next-line camelcase
    warning_seconds?: number,
    // eslint-disable-next-line camelcase
    warning_schedule_seconds?: number[]
}

export const adminMfaSlice = createSlice({
    name: 'adminMfa',
    initialState,
    reducers: {
        setAdminMfaChecking: state => ({
            ...state,
            status: 'checking' as AdminMfaStatus
        }),
        setAdminEligibility: (state, action: PayloadAction<{
            canAccessAdmin: boolean,
            canAccessWelfareAdmin: boolean
        }>) => ({
            ...state,
            ...action.payload
        }),
        setAdminMfaSession: (state, action: PayloadAction<AdminMfaSessionPayload>) => {
            const session = action.payload || {};

            if (!session.valid) {
                return {
                    ...state,
                    status: 'required' as AdminMfaStatus,
                    expiresAt: undefined,
                    expiresInSeconds: 0,
                    receivedAtMs: undefined,
                    // Converted to a deadline here so it stays correct however
                    // long it sits in the store. Undefined clears a block that
                    // has since lapsed.
                    blockedUntilMs: (session.blocked_in_seconds || 0) > 0
                        ? Date.now() + (session.blocked_in_seconds as number) * 1000
                        : undefined
                };
            }

            return {
                ...state,
                status: 'valid' as AdminMfaStatus,
                isMemberLocked: false,
                // A session cannot coexist with a lockout: verifying is what
                // ends one.
                blockedUntilMs: undefined,
                expiresAt: session.expires_at,
                expiresInSeconds: session.expires_in_seconds || 0,
                receivedAtMs: performance.now(),
                warningSeconds: session.warning_seconds || state.warningSeconds,
                warningSchedule: session.warning_schedule_seconds?.length
                    ? session.warning_schedule_seconds
                    : state.warningSchedule
            };
        },
        // The check did not come back — network down, 5xx, a timeout.
        //
        // Distinct from 'required' on purpose. 'required' means the server
        // answered and said no, which the modal can act on. This means we do not
        // know, and sending the member to the modal would strand them:
        // requesting a code goes over the same connection that just failed, so
        // they would sit at a prompt that cannot be satisfied. The gate offers a
        // retry instead.
        //
        // Still fail-closed — the screens stay unmounted either way.
        setAdminMfaCheckFailed: state => ({
            ...state,
            status: 'error' as AdminMfaStatus,
            expiresAt: undefined,
            expiresInSeconds: 0,
            receivedAtMs: undefined
        }),
        // Admin closed on purpose — the Lock now / Leave Admin button.
        //
        // Same status as an expiry, but the gate must not reopen the code
        // prompt over it: the member just said they were done, and mounting the
        // modal requests a fresh code on mount, so locking would email one and
        // spin as though they had asked to verify. They get the locked panel and
        // choose Verify me when they mean it.
        setAdminMfaLocked: state => ({
            ...state,
            status: 'required' as AdminMfaStatus,
            isMemberLocked: true,
            expiresAt: undefined,
            expiresInSeconds: 0,
            receivedAtMs: undefined
        }),
        setAdminMfaRequired: state => ({
            ...state,
            isMemberLocked: false,
            status: 'required' as AdminMfaStatus,
            expiresAt: undefined,
            expiresInSeconds: 0,
            receivedAtMs: undefined
        }),
        setAdminMfaChallenge: (state, action: PayloadAction<{
            maskedEmail?: string,
            nextAllowedAt?: string,
            codeExpiresAt?: string,
            devCode?: string,
            blockedUntilMs?: number
        }>) => ({
            ...state,
            ...action.payload
        }),
        clearAdminMfa: () => ({
            ...initialState
        })
    }
});

export const {
    setAdminMfaLocked,
    setAdminMfaChecking,
    setAdminMfaCheckFailed,
    setAdminEligibility,
    setAdminMfaSession,
    setAdminMfaRequired,
    setAdminMfaChallenge,
    clearAdminMfa
} = adminMfaSlice.actions;

export default adminMfaSlice.reducer;
